import { useEffect, useCallback, useRef } from 'react';
import { useCommunications } from '../context';

export const useCommunicationsPage = () => {
  const {
    communications,
    meta,
    loading,
    error,
    filters,
    selectedIds,
    selectAll,
    hasActiveFilters,
    selectedCount,
    contactId,
    CHANNEL_TYPES,
    fetchCommunications,
    updateFilters,
    clearFilters,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    handlePageChange,
    handleItemsPerPageChange,
    handleMarkAsRead,
    handleAssign,
    handleClose,
  } = useCommunications();

  // Track initial fetch and contact changes
  const initialFetchDone = useRef(false);
  const lastContactIdRef = useRef(null);

  // Initial fetch on mount or when contactId changes
  useEffect(() => {
    if (contactId) {
      if (lastContactIdRef.current !== contactId) {
        lastContactIdRef.current = contactId;
        initialFetchDone.current = true;
        fetchCommunications(1);
      }
    }
  }, [contactId, fetchCommunications]);

  // Refetch when filters change (but not on initial mount)
  useEffect(() => {
    if (!initialFetchDone.current) return;
    fetchCommunications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.channelType, filters.status, filters.sort]);

  // Filter handlers
  const handleSearchChange = useCallback(
    (value) => {
      updateFilters({ search: value });
    },
    [updateFilters]
  );

  const handleChannelTypeChange = useCallback(
    (value) => {
      updateFilters({ channelType: value });
    },
    [updateFilters]
  );

  const handleStatusChange = useCallback(
    (value) => {
      updateFilters({ status: value });
    },
    [updateFilters]
  );

  const handleSortChange = useCallback(
    (value) => {
      updateFilters({ sort: value });
    },
    [updateFilters]
  );

  // Refresh handler
  const handleRefresh = useCallback(() => {
    fetchCommunications(meta.current_page, meta.per_page);
  }, [fetchCommunications, meta.current_page, meta.per_page]);

  return {
    // State
    communications,
    meta,
    loading,
    error,
    filters,
    selectedIds,
    selectAll,
    hasActiveFilters,
    selectedCount,
    CHANNEL_TYPES,

    // Filter handlers
    handleSearchChange,
    handleChannelTypeChange,
    handleStatusChange,
    handleSortChange,

    // Selection
    toggleSelect,
    toggleSelectAll,
    clearSelection,

    // Actions
    handleMarkAsRead,
    handleAssign,
    handleClose,

    // Pagination
    handlePageChange,
    handleItemsPerPageChange,

    // Other
    handleRefresh,
    clearFilters,
  };
};

export default useCommunicationsPage;
